import { useMemo } from 'react';
import { AxiosResponse } from 'axios';
import { useGetAllJobs } from '@/common/api/endpoints/jobs/jobs';
import { useAuthStore } from '@/common/auth/store';
import { GenericApiResponse } from '@/common/api/models/genericApiResponse';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/**
 * A background job as surfaced to the lecturer job tray.
 * Mirrors the backend `Job` schema fields the tray renders.
 */
export interface Job {
  id: string;
  type: string;
  label?: string;
  status: string;
  currentStage: string;
  progress: number;
  result?: string | null;
  finished?: string | null;
  /** Job params (camelCase from API). Used to re-attach feature UIs to running jobs. */
  details?: {
    moduleId?: string;
    version?: string;
    leaves?: Array<{
      taskId?: string;
      taskTitle?: string;
      ruleId?: string;
    }>;
    [key: string]: unknown;
  };
}

export interface UserJobsData {
  allJobs: Job[];
  runningJobs: Job[];
  recentFailedJobs: Job[];
  hasRunningJobs: boolean;
  hasTrayAttention: boolean;
  trayBadgeCount: number;
}

const parseJobs = (response: AxiosResponse<GenericApiResponse> | undefined): Job[] => {
  const jobs = (response?.data?.data as unknown as Job[]) || [];
  return Array.isArray(jobs) ? jobs : [];
};

const isRecentFailure = (job: Job): boolean => {
  if (job.status !== 'failed' || !job.finished) return false;
  const finishedAt = new Date(job.finished).getTime();
  if (Number.isNaN(finishedAt)) return false;
  return Date.now() - finishedAt < TWENTY_FOUR_HOURS_MS;
};

const buildUserJobsData = (activeJobs: Job[], completedJobs: Job[]): UserJobsData => {
  const allJobs = [...activeJobs, ...completedJobs];
  const runningJobs = allJobs.filter((job) => job.status === 'running' || job.status === 'pending');
  const recentFailedJobs = completedJobs.filter(isRecentFailure);
  const trayJobIds = new Set([
    ...runningJobs.map((job) => job.id),
    ...recentFailedJobs.map((job) => job.id),
  ]);

  return {
    allJobs,
    runningJobs,
    recentFailedJobs,
    hasRunningJobs: runningJobs.length > 0,
    hasTrayAttention: trayJobIds.size > 0,
    trayBadgeCount: trayJobIds.size,
  };
};

/**
 * Polls the current user's background jobs for the job tray.
 *
 * Backend `GET /jobs` is owner-scoped, so this only returns the caller's jobs.
 * Fetches active (running/pending) and completed jobs so recent failures stay visible.
 * Polls faster while jobs are active to keep the tray responsive without
 * hammering the API when nothing is running.
 */
export const useUserJobs = () => {
  const user = useAuthStore((state) => state.user);
  const enabled = !!user;

  const refetchInterval = (activeJobs: Job[]) => {
    const hasActive = activeJobs.some((job) => job.status === 'running' || job.status === 'pending');
    return hasActive ? 15000 : 60000;
  };

  const activeQuery = useGetAllJobs(
    {},
    {
      query: {
        enabled,
        refetchInterval: (query) => {
          const responseData = query.state.data as AxiosResponse<GenericApiResponse> | undefined;
          return refetchInterval(parseJobs(responseData));
        },
      },
    },
  );

  const completedQuery = useGetAllJobs(
    { completed: true },
    {
      query: {
        enabled,
        refetchInterval: () => {
          const activeResponse = activeQuery.data as AxiosResponse<GenericApiResponse> | undefined;
          return refetchInterval(parseJobs(activeResponse));
        },
      },
    },
  );

  const data = useMemo(
    () => buildUserJobsData(parseJobs(activeQuery.data), parseJobs(completedQuery.data)),
    [activeQuery.data, completedQuery.data],
  );

  return {
    data,
    refetch: async () => {
      await Promise.all([activeQuery.refetch(), completedQuery.refetch()]);
    },
    isLoading: activeQuery.isLoading || completedQuery.isLoading,
    isFetching: activeQuery.isFetching || completedQuery.isFetching,
    isError: activeQuery.isError || completedQuery.isError,
    error: activeQuery.error ?? completedQuery.error,
  };
};
